import React from 'react';
import styled from 'styled-components';
import { SignIn } from '@clerk/clerk-react';
import { motion } from 'framer-motion';

const LoginContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  padding-top: 120px; /* Platz für Navbar */
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.2) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const LoginContent = styled.div`
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  padding: 2rem;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 240px); /* Vollständige Zentrierung */
`;

const LoginCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
    border-radius: 24px 24px 0 0;
  }
`;

const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
  width: 100%;
`;

const LoginTitle = styled.h1`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 2.5rem;
  margin: 0 auto 1rem auto;
  font-weight: 800;
  letter-spacing: -0.02em;
  text-align: center;
  width: fit-content;
`;

const LoginSubtitle = styled.p`
  color: ${props => props.theme.colors.textLight};
  font-size: 1.1rem;
  margin: 0 auto;
  font-weight: 500;
  text-align: center;
  max-width: 300px;
  line-height: 1.5;
`;

const ClerkContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  
  /* Clerk-spezifische Styling-Anpassungen */
  .cl-card {
    box-shadow: none !important;
    border: none !important;
    background: transparent !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  
  .cl-headerTitle {
    display: none !important;
  }
  
  .cl-headerSubtitle {
    display: none !important;
  }
  
  .cl-formFieldInput {
    border-radius: 12px !important;
    border: 2px solid #e2e8f0 !important;
    padding: 0.875rem 1rem !important;
    font-size: 1rem !important;
    transition: all 0.3s ease !important;
    width: 100% !important;
    text-align: center !important;
    
    &:focus {
      border-color: ${props => props.theme.colors.primary} !important;
      box-shadow: 0 0 0 3px rgba(46, 204, 113, 0.1) !important;
    }
  }
  
  .cl-formFieldLabel {
    color: ${props => props.theme.colors.text} !important;
    font-weight: 600 !important;
    margin-bottom: 0.5rem !important;
    text-align: center !important;
    display: block !important;
    width: 100% !important;
  }
  
  .cl-button {
    background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary}) !important;
    border: none !important;
    border-radius: 12px !important;
    padding: 0.75rem 2rem !important;
    font-weight: 600 !important;
    font-size: 0.95rem !important;
    transition: all 0.3s ease !important;
    width: auto !important;
    min-width: 180px !important;
    max-width: 250px !important;
    margin: 1rem auto 0 auto !important;
    display: block !important;
    text-align: center !important;
    justify-content: center !important;
    
    &:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 5px 15px rgba(46, 204, 113, 0.4) !important;
    }
  }
  
  .cl-footerActionLink {
    color: ${props => props.theme.colors.primary} !important;
    font-weight: 600 !important;
    text-align: center !important;
    display: block !important;
    width: 100% !important;
    font-size: 0.85rem !important;
    padding: 0.5rem 1rem !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
    
    &:hover {
      color: ${props => props.theme.colors.secondary} !important;
      background: rgba(102, 126, 234, 0.1) !important;
    }
  }
  
  .cl-dividerLine {
    background: #e2e8f0 !important;
    margin: 1.5rem 0 !important;
  }
  
  .cl-dividerText {
    color: ${props => props.theme.colors.textLight} !important;
    text-align: center !important;
    font-size: 0.9rem !important;
  }
  
  .cl-socialButtons {
    display: flex !important;
    gap: 0.75rem !important;
    justify-content: center !important;
    margin-bottom: 1.5rem !important;
  }
  
  .cl-socialButtonsBlockButton {
    flex: 0 0 auto !important;
    width: 120px !important;
    border-radius: 10px !important;
    padding: 0.625rem 0.75rem !important;
    font-weight: 600 !important;
    font-size: 0.9rem !important;
    transition: all 0.3s ease !important;
    
    &:hover {
      transform: translateY(-2px) !important;
    }
  }
  
  /* Spezifische Styling für Auth-Buttons */
  .cl-button[data-localization-key*="edit"],
  .cl-button[data-localization-key*="change"],
  .cl-button[data-localization-key*="back"],
  .cl-button[data-localization-key*="cancel"] {
    background: rgba(102, 126, 234, 0.1) !important;
    color: ${props => props.theme.colors.primary} !important;
    border: 1px solid rgba(102, 126, 234, 0.2) !important;
    padding: 0.5rem !important;
    font-size: 0.85rem !important;
    font-weight: 500 !important;
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    max-width: 40px !important;
    border-radius: 8px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 0 auto !important;
    
    &:hover {
      background: rgba(102, 126, 234, 0.2) !important;
      transform: translateY(-1px) !important;
    }
  }
`;

const BackToHome = styled.div`
  text-align: center;
  margin-top: 2.5rem;
  width: 100%;
`;

const BackLink = styled.a`
  color: ${props => props.theme.colors.textLight};
  text-decoration: none;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-block;
  margin: 0 auto;
  
  &:hover {
    color: white;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
  }
`;

const AdminLogin = () => {
  return (
    <LoginContainer>
      <LoginContent>
        <LoginCard
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <LoginHeader>
            <LoginTitle>Admin Login</LoginTitle>
            <LoginSubtitle>
              Melden Sie sich an, um auf das Admin-Dashboard zuzugreifen
            </LoginSubtitle>
          </LoginHeader>
          
          <ClerkContainer>
            <SignIn 
              appearance={{
                elements: {
                  card: 'cl-card',
                  headerTitle: 'cl-headerTitle',
                  headerSubtitle: 'cl-headerSubtitle',
                  formFieldInput: 'cl-formFieldInput',
                  formFieldLabel: 'cl-formFieldLabel',
                  button: 'cl-button',
                  footerActionLink: 'cl-footerActionLink',
                  dividerLine: 'cl-dividerLine',
                  dividerText: 'cl-dividerText'
                }
              }}
              redirectUrl="/admin"
              signUpUrl="/admin/sign-up"
            />
          </ClerkContainer>
          
          <BackToHome>
            <BackLink href="/">← Zurück zur Startseite</BackLink>
          </BackToHome>
        </LoginCard>
      </LoginContent>
    </LoginContainer>
  );
};

export default AdminLogin;
